import React, { useRef, useEffect } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Text, HStack, Badge, Box,
} from '@chakra-ui/react';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const COLORS = ['#2C7A7B', '#2B6CB0', '#6B46C1', '#C05621', '#276749', '#97266D', '#744210'];

// Fits map bounds to show all markers
const FitBounds = ({ areas }: { areas: any[] }) => {
  const map = useMap();
  useEffect(() => {
    const valid = areas.filter(a => a.location?.coordinates);
    if (valid.length === 0) return;
    if (valid.length === 1) {
      const [lng, lat] = valid[0].location.coordinates;
      map.setView([lat, lng], 13);
      return;
    }
    const bounds = L.latLngBounds(valid.map(a => [a.location.coordinates[1], a.location.coordinates[0]] as [number, number]));
    map.fitBounds(bounds, { padding: [60, 60] });
  }, [areas, map]);
  return null;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  areas: any[];
  providerName: string;
}

const CoverageMapModal: React.FC<Props> = ({ isOpen, onClose, areas, providerName }) => {
  const mapRef = useRef<L.Map>(null);
  const validAreas = areas.filter(a => a.location?.coordinates?.length === 2);

  useEffect(() => {
    if (isOpen) setTimeout(() => mapRef.current?.invalidateSize(), 200);
  }, [isOpen]);

  const defaultCenter: [number, number] = validAreas.length > 0
    ? [validAreas[0].location.coordinates[1], validAreas[0].location.coordinates[0]]
    : [20.5937, 78.9629];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent maxW="900px">
        <ModalHeader bg="teal.600" color="white" borderTopRadius="md" fontSize="md">
          🗺️ Coverage Map — {providerName}
          <Text fontSize="xs" fontWeight="normal" mt={1} opacity={0.85}>
            {validAreas.length} area{validAreas.length !== 1 ? 's' : ''} with location data
          </Text>
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody p={0}>
          {validAreas.length === 0 ? (
            <Box p={10} textAlign="center" color="gray.400">
              <Text fontSize="lg">No coverage areas with location data found.</Text>
            </Box>
          ) : (
            <>
              <MapContainer
                center={defaultCenter}
                zoom={11}
                style={{ height: '500px', width: '100%' }}
                ref={mapRef}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap / Leaflet"
                />
                <FitBounds areas={validAreas} />
                {validAreas.map((area, i) => {
                  const [lng, lat] = area.location.coordinates;
                  const color = COLORS[i % COLORS.length];
                  return (
                    <React.Fragment key={area._id || i}>
                      <Circle
                        center={[lat, lng]}
                        radius={(area.rangeInKm || 10) * 1000}
                        pathOptions={{ color, fillColor: color, fillOpacity: 0.12, weight: 2 }}
                      />
                      <Marker position={[lat, lng]}>
                        <Popup>
                          <Box minW="160px">
                            <Text fontWeight="bold" fontSize="sm" mb={1}>{area.name}</Text>
                            {area.city && <Text fontSize="xs" color="gray.500" mb={1}>{area.city}</Text>}
                            <HStack spacing={2} flexWrap="wrap">
                              <Badge colorScheme="teal" fontSize="xs">{area.rangeInKm} km</Badge>
                              {area.isHospital && <Badge colorScheme="green" fontSize="xs">Hospital</Badge>}
                              {area.ambulancePlaced && <Badge colorScheme="blue" fontSize="xs">Ambulance</Badge>}
                            </HStack>
                            <Text fontSize="xs" color="gray.400" mt={1}>
                              {lat.toFixed(5)}, {lng.toFixed(5)}
                            </Text>
                          </Box>
                        </Popup>
                      </Marker>
                    </React.Fragment>
                  );
                })}
              </MapContainer>

              {/* Legend */}
              <Box p={3} borderTopWidth={1} bg="gray.50" maxH="120px" overflowY="auto">
                <HStack spacing={3} flexWrap="wrap">
                  {validAreas.map((area, i) => (
                    <HStack key={area._id || i} spacing={1}>
                      <Box w="12px" h="12px" borderRadius="full" bg={COLORS[i % COLORS.length]} flexShrink={0} />
                      <Text fontSize="xs">{area.name} ({area.rangeInKm} km)</Text>
                    </HStack>
                  ))}
                </HStack>
              </Box>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CoverageMapModal;
